import {
  addAsset,
  addAssets,
  AssetListItem,
  AssetType,
  pointCloudType,
  CameraData,
  OrthoData,
  LayerData
} from "../redux/assets-slice";
import {addFolder} from '../redux/folders-slice';
import {dialog, fse, getTemporaryFile, glob} from "../electron-modules";
import path from 'path';
import slash from 'slash';
import {hideBackdrop, showBackdrop, toast} from "../app";
import {PythonExecutable} from "../executable";
import LocalScene from "../viewer/js/projections";
import { chunkedArray } from "../viewer/js/utilities";
import {getGlobalSettings} from "../settings";
import { loadProjectFile } from ".";
import {
  csvExportFilter,
  IFCFileFormat,
  imageCSVFormat,
  imageOrthoFormat,
  imagePlanarFormat,
  pointCloudFileFormats,
  configFileType,
  SHPFileFormat,
  DXFFileFormat
} from "../file-extensions";
import { t } from "../localization";
import { readFileBuffer, readFileText } from "../viewer/js/file-system-loader";
import { nanoid } from "@reduxjs/toolkit";

interface ImageDetectResults {
  readonly pathFound: boolean;
  readonly imagePath: string;
  readonly imageExt: string;
  readonly hasImageExt: boolean;
}

const DEFAULT_LAYER_COLOR = "FF0000";

export const pathWithExtension = (filename, extension) => {
  const basename = filename.replace(/\.[^.$]+$/, '');
  return `${basename}.${extension}`;
};

export const isAssetFile = (filename) => {
  return isPanoramicFile(filename)
    || isPlanarFile(filename)
    || isPointCloudFile(filename)
    || isOrthomosaicFile(filename)
    || isModelFile(filename);
};

export const isModelFile = (filename) => {
  return isIFCFile(filename)
    || isSHPFile(filename)
    || isDXFFile(filename);
};

export const isCameraFile = (filename) => {
  return isCSVFile(filename)
    || isTXTFile(filename);
};

export const isCSVFile = filename => {
  return path.extname(filename) === '.csv';
};

export const isTXTFile = filename => {
  return path.extname(filename) === '.txt';
};

export const isIFCFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === IFCFileFormat;
};

export const isSHPFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === SHPFileFormat;
};

export const isDXFFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === DXFFileFormat;
};

export const isConfigFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === configFileType;
};

export const isPanoramicFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return imageCSVFormat.indexOf(extension) !== -1;
};

export const isPlanarFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return imagePlanarFormat.indexOf(extension) !== -1;
};

export const isPointCloudFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return pointCloudFileFormats.indexOf(extension) !== -1;
};

export const isOrthomosaicFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return imageOrthoFormat.indexOf(extension) !== -1;
};

export const getNewDefaultFolder = (dispatch) => {
  // No folder has been provided, so a new folder is created instead
  const action = dispatch(addFolder("New Folder"));
  return action.payload.id;
};

export const addAssetFolder = async (dispatch, paths, folderID) => {
  const cloudPaths = paths.filter(isPointCloudFile);
  const cameraPaths = paths.filter(isCameraFile);
  const modelPaths = paths.filter(isModelFile);
  const orthoPaths = paths.filter(isOrthomosaicFile);
  const planarPaths = paths.filter(isPlanarFile);

  const numClouds = cloudPaths.length;
  const numModels = modelPaths.length;
  const numImageFiles = cameraPaths.length
    + orthoPaths.length
    + planarPaths.length;

  // Nothing to add, exit early
  if (numClouds === 0 && numImageFiles === 0 && numModels === 0) {
    return;
  }

  // Add all planar data
  const planarFilesAdded = await addPlanarFiles(
    dispatch, planarPaths, folderID);

  // Add all panoramic data
  const csvFilesAdded = await addCSVFiles(
    dispatch, cameraPaths, folderID);

  // Add all pointclouds
  const pointsAdded = addPointCloudFiles(dispatch, cloudPaths, folderID);

  // Add orthomosaic data
  const orthosAdded = await addOrthoFiles(dispatch, orthoPaths, folderID);

  // Add model files
  const modelsAdded = await addModelFiles(dispatch, modelPaths, folderID);

  const totalAdded = pointsAdded
    + csvFilesAdded
    + planarFilesAdded
    + orthosAdded
    + modelsAdded;

  if (totalAdded === 0) {
    return;
  }

  toast.success(t("toast.added-project-files", {
    count: totalAdded
  }));
};

export const addFilePaths = (dispatch, files, newFolderID) => {
  if (!files || files.length === 0) {
    return;
  }

  const configs = files.filter(isConfigFile);
  const assets = files.filter(isAssetFile);

  if (configs.length > 0) {
    // Load first config file (ignore other files)
    const success = loadProjectFile(configs[0]);
    if (success) {
      toast.success(t("toast.load-config-file"));
    }
    return;
  }

  if (assets.length > 0) {
    // Add new assets
    addAssetFolder(dispatch, assets, newFolderID);
  }
};

export const addModelFiles = async (dispatch, assetPaths, folderID) => {
  const ifcPaths = assetPaths.filter(isIFCFile);
  const shpPaths = assetPaths.filter(isSHPFile);
  const dxfPaths = assetPaths.filter(isDXFFile);

  let modelsAdded = 0;
  modelsAdded += await addIFCFiles(dispatch, ifcPaths, folderID);
  modelsAdded += await addSHPFiles(dispatch, shpPaths, folderID);
  modelsAdded += await addDXFFiles(dispatch, dxfPaths, folderID);

  return modelsAdded;
};

export const addPointCloudFiles = (dispatch, assetPaths, folderID) => {
  let assetsList = assetPaths.map(pointPath => {
    const extension = path.extname(pointPath).slice(1);
    const type = pointCloudType(extension);
    const name = path.basename(pointPath);

    if (type === AssetType.Encompass) {
      pointPath = path.dirname(pointPath);
      if (name !== "r.idx") {
        toast.error("Error loading points. Expected filename is r.idx");
        return null;
      }
    }

    if (type === AssetType.Potree) {
      pointPath = path.dirname(pointPath);
      if (name !== "octree.bin") {
        toast.error("Error loading points. Expected filename is octree.bin");
        return null;
      }
    }

    return {
      folderID,
      assetPath: pointPath,
      type
    } as AssetListItem;
  });

  assetsList = assetsList.filter(x => x!== null);

  dispatch(addAssets(assetsList));
  return assetPaths.length;
};

const addIFCFiles = async (dispatch, assetPaths, folderID) => {
  let assetsList = assetPaths.map(filePath => {
    return {
      folderID,
      assetPath: filePath,
      type: AssetType.IFC
    } as AssetListItem;
  });

  dispatch(addAssets(assetsList));
  return assetPaths.length;
};

const getDXFLayerNames = (text): Promise<{name: string, color: string}[]> => {
  return new Promise((resolve, reject) => {
    const workerURL = "workers/dxf-reader.js";
    let worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);

    worker.onmessage = event => {
      const {success, response} = event.data;

      if (success) {
        resolve(response);
      } else {
        console.error(response);
        reject(response);
      }
    };

    const data = {text, type: "layers"};
    worker.postMessage(data);
  });
};

const addDXFFiles = async (dispatch, assetPaths, folderID) => {
  let assetsList = [];

  showBackdrop();

  for (let assetPath of assetPaths) {
    try {
      const text = await readFileText(assetPath);
      const layers = await getDXFLayerNames(text);

      const layerData = layers.map(layer => {
        return {
          id: nanoid(),
          name: layer.name,
          visible: true,
          color: layer.color
        };
      }) as LayerData[];

      const assetData = {
        folderID,
        assetPath,
        data: layerData,
        type: AssetType.DXF
      } as AssetListItem;

      assetsList.push(assetData);
    } catch(err) {
      const fileName = path.basename(assetPath);
      toast.error(t("toast.import-dxf-error", {
        path: fileName
      }));
    }
  }

  hideBackdrop();

  dispatch(addAssets(assetsList));
  return assetsList.length;
};

const getSHPLayerNames = (buffer) : Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const workerURL = "workers/shp-reader.js";
    let worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);

    worker.onmessage = event => {
      const {success, response} = event.data;

      if (success) {
        resolve(response);
      } else {
        console.error(response);
        reject(response);
      }
    };

    const data = {dbf: buffer, type: "layers"};
    worker.postMessage(data, [data.dbf]);
  });
};

const addSHPFiles = async (dispatch, assetPaths, folderID) => {
  let assetsList = [];

  showBackdrop();

  for (let assetPath of assetPaths) {
    try {
      const dbfPath = pathWithExtension(assetPath, "dbf");
      const buffer = await readFileBuffer(dbfPath);
      const layers = await getSHPLayerNames(buffer);

      const layerData = layers.map(layer => {
        return {
          id: nanoid(),
          name: layer,
          visible: true,
          color: DEFAULT_LAYER_COLOR
        };
      }) as LayerData[];

      const assetData = {
        folderID,
        assetPath,
        data: layerData,
        type: AssetType.SHP
      } as AssetListItem;

      assetsList.push(assetData);
    } catch(err) {
      const fileName = path.basename(assetPath);
      toast.error(t("toast.import-shp-error", {
        path: fileName
      }));
    }
  }

  hideBackdrop();

  dispatch(addAssets(assetsList));
  return assetsList.length;
};

export const addOrthoFiles = async (dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = await addOrthoBatch(dispatch,
    assetPaths, folderID);

  hideBackdrop();

  return numAdded;
};

export const addCSVFiles = async (dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = 0;

  for (const assetPath of assetPaths) {
    try {
      await addCameraFile(dispatch, assetPath, folderID);
      numAdded++;
      continue;
    } catch {}

    try {
      await addTagCSV(dispatch, assetPath, folderID);
      numAdded++;
      continue;
    } catch {}
  }

  hideBackdrop();

  return numAdded;
};

export const addCameraFiles = async (dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = 0;

  for (const assetPath of assetPaths) {
    try {
      await addCameraFile(dispatch, assetPath, folderID);
      numAdded += 1;
    } catch (event) {
      const fileName = path.basename(assetPath);

      if (event.pathError) {
        toast.error(t("toast.import-csv-path-error", {
          path: fileName
        }));
      } else {
        toast.error(t("toast.import-csv-parse-error", {
          path: fileName
        }));
      }
    } finally {
      hideBackdrop();
    }
  }

  return numAdded;
};

export const addPlanarFiles = async (dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = 0;
  for (const assetPath of assetPaths) {
    const added = await addPlanarFile(dispatch, assetPath, folderID);
    if (added) {
      numAdded += 1;
    } else {
      const fileName = path.basename(assetPath);
      toast.error(t("toast.import-planar-error", {
        path: fileName
      }));
    }
  }

  hideBackdrop();

  return numAdded;
};

export const addOrthoBatch = async (dispatch, assetPaths, folderID): Promise<number> => {
  if (assetPaths.length === 0) {
    return 0;
  }

  return new Promise(async resolve => {
    const exe = new PythonExecutable();
    const orthoQuality = await getGlobalSettings("orthoQuality");

    let response;
    let commands = [
      "-p", "convert_geotiff",
      "--image_paths", assetPaths,
      "--output_scale", orthoQuality
    ];

    let numAdded = 0;

    exe.run({
      command: commands,
      onLine: jsonData => {
        if (jsonData) {
          response = jsonData;
        }
      },
      onClose: () => {
        if (!response) {
          response = [];
        }

        let assetsList = [];

        response.forEach(geotiff => {
          const assetPath = slash(geotiff.file_path);

          if (geotiff.error) {
            toast.error(t(`toast.${geotiff.error}`, {
              path: path.basename(assetPath)
            }));
            return;
          }

          const {corners, base64, width, height, base64_split,
            scale_x, scale_y, width_original, height_original} = geotiff;

          const data = {
            imageBounds: corners,
            imageDataSplit: base64_split,
            imageData: base64,
            imageSize: [width, height],
            imageSizeOriginal: [width_original, height_original],
            imagePath: assetPath,
            scaleX: scale_x,
            scaleY: scale_y,
          } as OrthoData;

          assetsList.push({
            folderID,
            assetPath,
            type: AssetType.OrthoMosaic,
            data
          } as AssetListItem);

          numAdded += 1;
        });

        dispatch(addAssets(assetsList));
        resolve(numAdded);
      }
    });
  });
};

const addPlanarFile = (dispatch, assetPath, folderID): Promise<any> => {
  return new Promise(async resolve => {
    const text = await fse.readFile(assetPath, {encoding: 'utf8'});

    let config;
    let cameras;

    try {
      let decoded = JSON.parse(text);
      config = decoded.config;
      cameras = decoded.data;
    } catch {
      resolve(false);
      return;
    }

    const numCameras = cameras.length;
    if (numCameras === 0) {
      resolve(false);
      return;
    }

    let cameraNames = cameras.map(x => x[0]);
    let {pathFound, imagePath, imageExt, hasImageExt}
      = await detectImagePath(assetPath, cameraNames);

    if (!pathFound) {
      resolve(false);
      return;
    }

    let cameraData: CameraData[] = [];

    for (let i=0;i<numCameras;i++) {
      const camera = cameras[i];
      const filename = camera[0];
      const filePath = buildFilePath(imagePath, filename,
        imageExt, hasImageExt);

      const rowData = {
        path: filePath,
        name: path.basename(filePath),
        x: camera[1],
        y: camera[2],
        z: camera[3],
        roll: -1*camera[4],
        pitch: -1*camera[5],
        yaw: -1*camera[6],
        config: config
      } as CameraData;

      cameraData.push(rowData);
    }

    dispatch(addAsset(folderID, assetPath, AssetType.Planar, cameraData));
    resolve(true);
  });
};

const addCameraFile = (dispatch, assetPath, folderID): Promise<any> => {
  return new Promise((resolve, reject) => {
    const exe = new PythonExecutable();

    let response;
    const inputPath = assetPath;
    const outputPath = getTemporaryFile("csv");
    const projection = LocalScene.engineStyleProjection(LocalScene.dataProjection);

    let commands = [
      "-p", "convert_encompass_csv",
      "--input_file", inputPath,
      "--output_file", outputPath,
      "--projection", projection
    ];

    exe.run({
      command: commands,
      onLine: jsonData => {
        response = jsonData;
      },
      onClose: async () => {
        if (!response) {
          reject({pathError: false});
          return;
        }

        let cameraData: CameraData[] = [];

        const cameras = response.csv_data;
        const estimatedPath = response.estimated_path;

        const numCameras = cameras.length;
        if (numCameras === 0) {
          reject({pathError: false});
          return;
        }

        let cameraNames = cameras.map(x => x.name);
        let {pathFound, imagePath, imageExt, hasImageExt}
          = await detectImagePath(assetPath, cameraNames, estimatedPath);

        if (!pathFound) {
          reject({pathError: true});
          return;
        }

        // Update final camera data path / filenames
        for (let i=0;i<numCameras;i++) {
          const camera = cameras[i];
          const filename = cameras[i].name;
          const filePath = buildFilePath(imagePath, filename,
            imageExt, hasImageExt);

          const rowData = {
            path: filePath,
            name: path.basename(filePath),
            x: camera.x,
            y: camera.y,
            z: camera.z,
            roll: camera.roll,
            pitch: camera.pitch,
            yaw: camera.yaw
          } as CameraData;

          cameraData.push(rowData);
        }

        dispatch(addAsset(folderID, assetPath, AssetType.Panoramic, cameraData));
        resolve(true);
      }
    });
  });
};

export const openDialogMulti = (filter): Promise<string[]> => {
  return dialog.showOpenDialog( {
    filters: filter,
    properties: ['openFile', 'multiSelections']
  }).then(result => {
    if (result.canceled) {
      return [];
    }

    const filePaths = result.filePaths.map(path => slash(path));
    return filePaths;
  }).catch(() => {
    dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-load-error"));
    return [];
  });
};

export const openDialogFile = (filter): Promise<string> => {
  return dialog.showOpenDialog( {
    filters: filter,
    properties: ['openFile']
  }).then(result => {
    if (result.canceled) {
      return null;
    }

    const filePath = slash(result.filePaths[0]);
    return filePath;
  }).catch(() => {
    dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-load-error"));
    return null;
  });
};

export const openDialogFolder = () => {
  return dialog.showOpenDialog({
    properties: ['openDirectory']
  }).then(result => {
    if (result.canceled) {
      return {
        directoryPath: '',
        assetPaths: Promise.resolve([])
      };
    }

    const filePath = slash(result.filePaths[0]);

    return {
      directoryPath: filePath,
      assetPaths: parseDirectory(filePath)
    };
  }).catch(() => {
    dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-load-error"));
    return null;
  });
};

export const exportDialogCSV = (filename): Promise<string> => {
  return dialog.showSaveDialog( {
    defaultPath: filename,
    filters: csvExportFilter,
  }).then(result => {
    if (result.canceled) {
      return;
    }

    const savePath = slash(result.filePath);
    return savePath;
  }).catch(() => {
    dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-export-error"));
    return null;
  });
};

export const openDirectory = (): Promise<any> => {
  return dialog.showOpenDialog({
    properties: ["openDirectory"]
  }).then(result => {
    if (result.canceled) {
      return null;
    }
    if (result.filePaths) {
      const outputFolder = slash(result.filePaths[0]);
      return outputFolder;
    }

    return null;
  });
};

const parseDirectory = async (directoryPath: string): Promise<string[]> => {
  const entries = await fse.readdir(directoryPath, {withFileTypes: true});

  const files = entries
    .filter(entry => entry.isFile())
    .map(file => path.join(directoryPath, file.name));

  const directories = entries
    .filter(entry => entry.isDirectory())
    .map(directory => path.join(directoryPath, directory.name));

  for (let folder of directories) {
    files.push(...await parseDirectory(folder));
  }

  return files;
};

const checkImagePath = (directory, filenames, suffix, depth = 3) => {
  const results = {
    success: false,
    duplicate: false,
    imagePath: null,
    imageExt: null
  };

  const singleFolder = depth === 1;

  let dirSeperator = "";
  for (var i = 0; i < depth; i++) {
    if (i > 0) {
      dirSeperator += "/*";
    }

    let paths;

    if (singleFolder) {
      for (let subset of chunkedArray(filenames, 500)) {
        const filenameQuery = `{${subset.join(",")}}`;
        const globPath = `${dirSeperator}/${filenameQuery}${suffix}`;
        paths = glob.sync(globPath, {
          cwd: directory,
          root: directory
        });

        if (paths.length > 0) {
          break;
        }
      }
    } else {
      const globPath = `${dirSeperator}/${filenames[0]}${suffix}`;
      paths = glob.sync(globPath, {
        cwd: directory,
        root: directory
      });
    }

    let singleImageValid = paths.length === 1;
    let multiImageValid = singleFolder && (paths.length > 0);

    if (singleImageValid || multiImageValid) {
      results.success = true;
      results.imagePath = path.dirname(paths[0]);
      results.imageExt = path.extname(paths[0]);
      return results;
    } else if (paths.length >= 2) {
      results.duplicate = true;
    }

  }

  return results;
};

export const buildFilePath = (imagePath, filename, imageExt, hasImageExt) => {
  let filePath = hasImageExt ?
    path.join(imagePath, filename) :
    path.join(imagePath, `${filename}${imageExt}`);

  // Fix for windows UNC paths
  const isNetworkPath = imagePath.indexOf("//") === 0;
  if (isNetworkPath) {
    filePath = `/${filePath}`;
  }

  return filePath;
};

export const detectImagePath = async (assetPath: string, imageNames: string[], estimatedPath=null): Promise<ImageDetectResults> => {
  const directory = path.dirname(assetPath);
  const testImageExt = path.extname(imageNames[0]).toLowerCase();
  const hasImageExt = [".jpg", ".png", ".jpeg"].includes(testImageExt);
  const suffix = hasImageExt ? "" : ".{png,jpg,jpeg}";

  let response = {
    pathFound: false,
    imagePath: null,
    imageExt: null,
    hasImageExt
  };

  // Search for image directory using csv path (or use pre-calculated estimated path)
  let {success, duplicate, imagePath, imageExt} = estimatedPath
    ? estimatedPath
    : checkImagePath(directory, imageNames, suffix);;

  // Update response values
  response.pathFound = success;
  response.imagePath = imagePath;
  response.imageExt = imageExt;

  console.log("Detected image path: ", imagePath);

  if (!response.pathFound) {
    if (duplicate) {
      dialog.showErrorBox(assetPath, t("dialog.multiple-path-error")
      );
    } else {
      dialog.showErrorBox(assetPath, t("dialog.missing-path-error"));
    }

    const results = dialog.showOpenDialogSync({
      title: t("dialog.select-image-path"),
      properties: ['openDirectory']
    });

    if (results) {
      // Search for image directory using selected folder
      imagePath = slash(results[0]);
      let manual = checkImagePath(imagePath, imageNames, suffix, 1);

      // Update response values
      response.pathFound = manual.success;
      response.imagePath = imagePath;
      response.imageExt = manual.imageExt;

      if (!manual.success) {
        dialog.showErrorBox(imagePath, t("dialog.manual-path-error"));
      }
    }
  }

  return response;
};

export const addTagCSV = async (dispatch, csvPath, folderID) => {
  return new Promise((resolve, reject) => {
    let results = {
      valid_tags: [],
      invalid_rows: [],
      missing_headers: []
    };

    new PythonExecutable().run({
      command: ['-p', 'parse_tag_csv', '--path', csvPath],
      onLine: data => {
        if (data) {
          results = data;
        }
      },
      onClose: () => {
        if (results.valid_tags.length === 0) {
          reject({reason: 'missing_headers', data: results.missing_headers});
        } else if (results.invalid_rows.length > 0) {
          reject({reason: 'invalid_rows', data: results.invalid_rows});
        } else if (results.missing_headers.length > 0) {
          reject({reason: 'no_data'});
        } else {
          const tagData = results.valid_tags;
          dispatch(addAsset(folderID, csvPath, AssetType.Tag, tagData));
          resolve(results.valid_tags);
        }
      }
    });
  });
};